import { useEffect, useState } from 'react';
import { Check, ChevronDown, ChevronRight, Copy, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/core/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/core/components/ui/alert-dialog';
import type { PreviewPublishResult } from '../../stores/kiosk-flow-store';
import { PublishValidationErrors } from './PublishValidationErrors';

function formatJson(json: string): string {
  try {
    return JSON.stringify(JSON.parse(json), null, 2);
  } catch {
    return json;
  }
}

interface PublishFlowDialogProps {
  open: boolean;
  operatorName: string;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  // Asks the backoffice to prepare the saved draft — nothing is pushed or recorded
  loadPreview: () => Promise<PreviewPublishResult>;
}

// Publishing restarts every kiosk of the operator (notify=true), so it needs an explicit confirm.
// Preview JSON shows exactly what would be sent to the kiosks, loaded on demand.
export function PublishFlowDialog({ open, operatorName, onOpenChange, onConfirm, loadPreview }: PublishFlowDialogProps) {
  const [showJson, setShowJson] = useState(false);
  const [preview, setPreview] = useState<PreviewPublishResult | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [copied, setCopied] = useState(false);
  const [requestKey, setRequestKey] = useState(0);

  // Every open starts collapsed with no preview — the draft may have changed since last time
  useEffect(() => {
    if (open) return;
    setShowJson(false);
    setPreview(null);
    setCopied(false);
  }, [open]);

  useEffect(() => {
    if (!open || !showJson) return;
    let cancelled = false;
    setIsLoadingPreview(true);
    setPreview(null);
    setCopied(false);
    loadPreview().then((next) => {
      if (cancelled) return;
      setPreview(next);
      setIsLoadingPreview(false);
    });
    return () => {
      cancelled = true;
      setIsLoadingPreview(false);
    };
  }, [open, showJson, requestKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatted = preview && 'json' in preview ? formatJson(preview.json) : null;
  // The publish would fail with the same errors, so don't offer it
  const hasValidationErrors = !!preview && 'error' in preview && preview.validationErrors.length > 0;

  const handleCopy = async () => {
    if (!formatted) return;
    try {
      await navigator.clipboard.writeText(formatted);
      setCopied(true);
    } catch {
      toast.error('Could not copy to the clipboard');
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className={showJson ? 'max-w-3xl' : undefined}>
        <AlertDialogHeader>
          <AlertDialogTitle>Publish and restart kiosks?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2">
              <p>
                Every kiosk of <span className="font-medium text-gray-900">{operatorName}</span> restarts right away
                with the saved flow — even in the middle of a session.
              </p>
              <p>Offline kiosks get the flow the next time they restart.</p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-2">
          <Button variant="outline" size="sm" onClick={() => setShowJson((v) => !v)} aria-expanded={showJson}>
            {showJson ? <ChevronDown className="mr-2 h-4 w-4" /> : <ChevronRight className="mr-2 h-4 w-4" />}
            Preview JSON
          </Button>

          {showJson && isLoadingPreview && (
            <div className="flex items-center justify-center gap-2 py-8 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Preparing…
            </div>
          )}

          {showJson && preview && 'error' in preview && (
            <div className="space-y-2">
              {hasValidationErrors ? (
                <PublishValidationErrors errors={preview.validationErrors} />
              ) : (
                <p className="text-sm text-red-600">{preview.error}</p>
              )}
              <Button variant="outline" size="sm" onClick={() => setRequestKey((k) => k + 1)}>
                <RefreshCw className="mr-2 h-3 w-3" />
                Retry
              </Button>
            </div>
          )}

          {showJson && formatted && (
            <div className="relative">
              <pre className="max-h-[50vh] overflow-auto rounded-md border bg-gray-50 p-3 pr-24 font-mono text-xs text-gray-800">
                {formatted}
              </pre>
              <Button variant="outline" size="sm" className="absolute right-2 top-2 h-7" onClick={handleCopy}>
                {copied ? <Check className="mr-1 h-3 w-3" /> : <Copy className="mr-1 h-3 w-3" />}
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} disabled={hasValidationErrors}>
            Publish
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
